import { Loader2, UserRound, UsersRound, X } from 'lucide-react';
import React, { type KeyboardEvent, type ReactNode, useRef } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import { useDebounce } from '@apps/web/hooks/use_debounce';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { formatPhoneNumber } from '@apps/web/lib/phone_number_formatting';
import { normalizeValidPhoneNumberToE164 } from '@apps/web/lib/phone_number_normalization';
import { cn } from '@apps/web/lib/utils';
import {
    useOrganizationCaregivers,
    useOrganizationClientRecipients,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type ApiResponseType } from '@libraries/typescript/api_client';

export type ConversationRecipientCaregiver = {
    readonly id: string;
    readonly name: string;
    readonly office_name?: string | null;
    readonly phoneNumber?: string;
};

type ClientRecipientSearchResult = ApiResponseType<
    '/organizations/{organization_id}/clients/recipient-search',
    'get'
>['clients'][number];

export type ConversationRecipientClient = {
    readonly id: string;
    readonly recipientKey: string;
    readonly kind: 'client' | 'client_contact';
    readonly name: string;
    readonly clientName: string;
    readonly officeName: string | null;
    readonly relation: string | null;
    readonly phoneNumber: string;
    readonly phoneNumbers: ReadonlyArray<
        ClientRecipientSearchResult['phone_numbers'][number]
    >;
};

export type ConversationRecipient =
    | {
          readonly kind: 'caregiver';
          readonly caregiver: ConversationRecipientCaregiver;
      }
    | { readonly kind: 'client'; readonly client: ConversationRecipientClient }
    | { readonly kind: 'phoneNumber'; readonly phoneNumber: string };

export function conversationRecipientLabel(
    recipient: ConversationRecipient,
    formatName: (name: string | null | undefined) => string,
): string {
    // A client contact's name is the contact's own, so it keeps its order.
    return recipient.kind === 'caregiver'
        ? formatName(recipient.caregiver.name)
        : recipient.kind === 'client'
          ? recipient.client.kind === 'client'
              ? formatName(recipient.client.name)
              : recipient.client.name
          : formatPhoneNumber(recipient.phoneNumber);
}

export function conversationRecipientPhone(
    recipient: ConversationRecipient | null,
): string | null {
    return recipient?.kind === 'client'
        ? recipient.client.phoneNumber
        : recipient?.kind === 'phoneNumber'
          ? recipient.phoneNumber
          : (recipient?.caregiver.phoneNumber ?? null);
}

function clientContext(client: ConversationRecipientClient): string {
    return client.kind === 'client_contact'
        ? (client.relation?.trim() || 'Contact') + ' · ' + client.clientName
        : 'Client' + (client.officeName ? ' · ' + client.officeName : '');
}

function selectClientRecipient(
    client: ClientRecipientSearchResult,
    phoneNumber: string,
): ConversationRecipientClient {
    return {
        id: client.id,
        recipientKey: client.recipient_key,
        kind: client.kind,
        name: client.name,
        clientName: client.client_name,
        officeName: client.office_name,
        relation: client.relation,
        phoneNumbers: client.phone_numbers,
        phoneNumber,
    };
}

type ConversationRecipientPickerProps = {
    readonly organizationId: string;
    readonly disabled: boolean;
    readonly search: string;
    readonly onSearchChange: (search: string) => void;
    readonly recipient: ConversationRecipient | null;
    readonly onRecipientChange: (
        recipient: ConversationRecipient | null,
    ) => void;
    readonly variant: 'stacked' | 'header';
    readonly searchReason: string;
    readonly trailing?: ReactNode;
};

export function ConversationRecipientPicker({
    organizationId,
    disabled,
    search,
    onSearchChange,
    recipient,
    onRecipientChange,
    variant,
    searchReason,
    trailing,
}: ConversationRecipientPickerProps) {
    const { formatName } = useFormatPersonName();
    const inputRef = useRef<HTMLInputElement>(null);
    const resultsRef = useRef<HTMLDivElement>(null);
    const debouncedSearch = useDebounce(search, 180);
    const {
        data: caregivers = [],
        isLoading,
        error: caregiverError,
        mutate: retryCaregivers,
    } = useOrganizationCaregivers(
        organizationId,
        { reason: searchReason },
        { search: debouncedSearch, per_page: 8 },
    );
    const {
        data,
        isLoading: clientsLoading,
        error: clientError,
        mutate: retryClients,
    } = useOrganizationClientRecipients(
        organizationId,
        { reason: searchReason },
        { search: debouncedSearch, limit: 25 },
    );
    const clientRows = data?.clients ?? [];
    const phoneCandidate = normalizeValidPhoneNumberToE164(search);
    const isHeader = variant === 'header';
    const isSearching =
        search !== debouncedSearch || isLoading || clientsLoading;
    const showMatches =
        recipient === null && (!isHeader || search.trim() !== '');
    const choose = (next: ConversationRecipient) => {
        onRecipientChange(next);
    };
    const moveResultFocus = (event: KeyboardEvent<HTMLElement>) => {
        const buttons = Array.from(
            resultsRef.current?.querySelectorAll<HTMLButtonElement>(
                'button[data-recipient]:not(:disabled)',
            ) ?? [],
        );
        const current = buttons.findIndex(
            (button) => button === document.activeElement,
        );
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const next =
                event.key === 'ArrowDown'
                    ? (current + 1) % buttons.length
                    : current <= 0
                      ? buttons.length - 1
                      : current - 1;
            buttons[next]?.focus();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            onSearchChange('');
            inputRef.current?.focus();
        }
    };

    if (recipient !== null) {
        const selectedClient =
            recipient.kind === 'client' ? recipient.client : null;
        return (
            <div
                className={cn(
                    'flex shrink-0 items-start gap-3',
                    isHeader ? 'border-b px-4 py-3' : 'rounded-lg border p-3',
                )}
            >
                <span className="text-muted-foreground pt-0.5 text-sm">
                    To:
                </span>
                <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm font-medium break-words">
                            {conversationRecipientLabel(recipient, formatName)}
                        </span>
                        <Badge variant="secondary">
                            {selectedClient?.kind === 'client_contact'
                                ? 'Client contact'
                                : recipient.kind === 'client'
                                  ? 'Client'
                                  : recipient.kind === 'caregiver'
                                    ? 'Caregiver'
                                    : 'Phone number'}
                        </Badge>
                    </div>
                    {selectedClient ? (
                        <p className="text-muted-foreground text-xs break-words">
                            {clientContext(selectedClient)}
                        </p>
                    ) : null}
                    {selectedClient &&
                    selectedClient.phoneNumbers.length > 1 ? (
                        <label className="block">
                            <span className="sr-only">Texting number</span>
                            <select
                                className="border-input bg-background focus-visible:ring-ring mt-1 max-w-full rounded-md border px-2 py-1.5 text-sm focus-visible:ring-2"
                                disabled={disabled}
                                onChange={(event) =>
                                    choose({
                                        kind: 'client',
                                        client: {
                                            ...selectedClient,
                                            phoneNumber: event.target.value,
                                        },
                                    })
                                }
                                value={selectedClient.phoneNumber}
                            >
                                {selectedClient.phoneNumbers.map((phone) => (
                                    <option
                                        disabled={!!phone.unavailable_reason}
                                        key={phone.number}
                                        value={phone.number}
                                    >
                                        {phone.label +
                                            ' · ' +
                                            formatPhoneNumber(phone.number) +
                                            (phone.unavailable_reason
                                                ? ' — ' +
                                                  phone.unavailable_reason
                                                : '')}
                                    </option>
                                ))}
                            </select>
                        </label>
                    ) : conversationRecipientPhone(recipient) ? (
                        <p className="text-muted-foreground text-xs">
                            {
                                selectedClient?.phoneNumbers.find(
                                    (phone) =>
                                        phone.number ===
                                        selectedClient.phoneNumber,
                                )?.label
                            }
                            {selectedClient ? ' · ' : ''}
                            {formatPhoneNumber(
                                conversationRecipientPhone(recipient) ?? '',
                            )}
                        </p>
                    ) : null}
                </div>
                <Button
                    aria-label="Change recipient"
                    className="shrink-0"
                    disabled={disabled}
                    onClick={() => {
                        onRecipientChange(null);
                        onSearchChange('');
                    }}
                    size="sm"
                    type="button"
                    variant="ghost"
                >
                    Change
                </Button>
                {trailing}
            </div>
        );
    }

    return (
        <div
            className={cn(
                'relative shrink-0',
                isHeader ? 'border-b px-4 py-3' : '',
            )}
        >
            <div className="flex items-center gap-3">
                <label
                    className="text-muted-foreground shrink-0 text-sm"
                    htmlFor={searchReason + '-recipient'}
                >
                    To:
                </label>
                <Input
                    aria-label="Search message recipients"
                    autoComplete="off"
                    autoFocus={true}
                    className={cn(
                        'min-w-0 flex-1 text-sm',
                        isHeader
                            ? 'border-none bg-transparent px-0 shadow-none focus-visible:ring-0'
                            : '',
                    )}
                    disabled={disabled}
                    id={searchReason + '-recipient'}
                    onChange={(event) => onSearchChange(event.target.value)}
                    onKeyDown={moveResultFocus}
                    placeholder="Type a name or phone number…"
                    ref={inputRef}
                    value={search}
                />
                {search && !isHeader ? (
                    <Button
                        aria-label="Clear search"
                        onClick={() => onSearchChange('')}
                        size="icon"
                        type="button"
                        variant="ghost"
                    >
                        <X className="size-4" />
                    </Button>
                ) : null}
                {trailing}
            </div>
            {showMatches ? (
                <div
                    aria-label="Matching recipients"
                    className={cn(
                        'bg-popover z-20 overflow-y-auto rounded-lg border text-sm',
                        isHeader
                            ? 'absolute top-full right-4 left-4 max-h-[min(28rem,55dvh)] shadow-lg'
                            : 'mt-3 max-h-64',
                    )}
                    onKeyDown={moveResultFocus}
                    ref={resultsRef}
                    role="group"
                >
                    {isSearching ? (
                        <p
                            className="text-muted-foreground flex items-center gap-2 p-4"
                            role="status"
                        >
                            <Loader2 className="size-4 animate-spin" />
                            Searching…
                        </p>
                    ) : (
                        <React.Fragment>
                            {clientError || caregiverError ? (
                                <div className="p-3" role="alert">
                                    <p>
                                        Some recipients couldn’t be loaded. Try
                                        searching again.
                                    </p>
                                    <Button
                                        onClick={() => {
                                            if (caregiverError) {
                                                void retryCaregivers();
                                            }
                                            if (clientError) {
                                                void retryClients();
                                            }
                                        }}
                                        size="sm"
                                        type="button"
                                        variant="ghost"
                                    >
                                        Retry
                                    </Button>
                                </div>
                            ) : null}
                            {clientRows.length > 0 ? (
                                <div>
                                    <p className="text-muted-foreground px-3 pt-3 pb-1 text-xs font-medium">
                                        Clients & contacts
                                    </p>
                                    {clientRows.map((client) => {
                                        const selectedNumber =
                                            client.phone_numbers.find(
                                                (phone) =>
                                                    phone.number ===
                                                    client.phone_number,
                                            );
                                        const unavailable =
                                            !client.phone_number;
                                        const reason =
                                            client.phone_numbers[0]
                                                ?.unavailable_reason ??
                                            'No phone number';
                                        return (
                                            <button
                                                className="hover:bg-accent focus-visible:bg-accent focus-visible:ring-ring flex w-full items-start gap-3 px-3 py-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset disabled:cursor-default disabled:opacity-60"
                                                data-recipient={true}
                                                disabled={
                                                    disabled || unavailable
                                                }
                                                key={
                                                    client.id +
                                                    ':' +
                                                    client.recipient_key
                                                }
                                                onClick={() => {
                                                    if (client.phone_number) {
                                                        choose({
                                                            kind: 'client',
                                                            client: selectClientRecipient(
                                                                client,
                                                                client.phone_number,
                                                            ),
                                                        });
                                                    }
                                                }}
                                                type="button"
                                            >
                                                <span className="bg-muted text-muted-foreground mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full">
                                                    {client.kind ===
                                                    'client_contact' ? (
                                                        <UsersRound className="size-4" />
                                                    ) : (
                                                        <UserRound className="size-4" />
                                                    )}
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="flex flex-wrap items-center gap-2">
                                                        <span className="font-medium break-words">
                                                            {client.kind ===
                                                            'client'
                                                                ? formatName(
                                                                      client.name,
                                                                  )
                                                                : client.name}
                                                        </span>
                                                        <Badge variant="secondary">
                                                            {client.kind ===
                                                            'client_contact'
                                                                ? 'Client contact'
                                                                : 'Client'}
                                                        </Badge>
                                                        {client.is_primary ? (
                                                            <span className="text-muted-foreground text-xs">
                                                                Primary
                                                            </span>
                                                        ) : null}
                                                    </span>
                                                    <span className="text-muted-foreground mt-0.5 block text-xs break-words">
                                                        {client.kind ===
                                                        'client_contact'
                                                            ? (client.relation?.trim() ||
                                                                  'Contact') +
                                                              ' · ' +
                                                              client.client_name
                                                            : client.office_name}
                                                    </span>
                                                    <span className="text-muted-foreground mt-1 block text-xs">
                                                        {unavailable
                                                            ? reason
                                                            : (selectedNumber?.label ??
                                                                  'Phone') +
                                                              ' · ' +
                                                              formatPhoneNumber(
                                                                  client.phone_number ??
                                                                      '',
                                                              )}
                                                        {!unavailable &&
                                                        client.phone_numbers
                                                            .length > 1
                                                            ? ' · More numbers'
                                                            : ''}
                                                    </span>
                                                </span>
                                            </button>
                                        );
                                    })}
                                    {data?.has_more ? (
                                        <p className="text-muted-foreground px-3 py-2 text-xs">
                                            More matches available. Add a name
                                            or phone digits to narrow your
                                            search.
                                        </p>
                                    ) : null}
                                </div>
                            ) : null}
                            {caregivers.length > 0 ? (
                                <div>
                                    <p className="text-muted-foreground px-3 pt-3 pb-1 text-xs font-medium">
                                        Caregivers
                                    </p>
                                    {caregivers.map((caregiver) => (
                                        <button
                                            className="hover:bg-accent focus-visible:bg-accent focus-visible:ring-ring flex w-full flex-col gap-1 px-3 py-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset"
                                            aria-label={[
                                                formatName(caregiver.name),
                                                caregiver.office_name,
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                            data-recipient={true}
                                            disabled={disabled}
                                            key={caregiver.id}
                                            onClick={() =>
                                                choose({
                                                    kind: 'caregiver',
                                                    caregiver,
                                                })
                                            }
                                            type="button"
                                        >
                                            <span className="font-medium">
                                                {formatName(caregiver.name)}
                                            </span>
                                            {caregiver.office_name ? (
                                                <span className="text-muted-foreground text-xs">
                                                    {caregiver.office_name}
                                                </span>
                                            ) : null}
                                        </button>
                                    ))}
                                </div>
                            ) : null}
                            {phoneCandidate ? (
                                <button
                                    aria-label={
                                        'Text ' +
                                        formatPhoneNumber(phoneCandidate)
                                    }
                                    className="hover:bg-accent focus-visible:bg-accent flex w-full flex-col gap-1 border-t px-3 py-3 text-left outline-none"
                                    data-recipient={true}
                                    disabled={disabled}
                                    onClick={() =>
                                        choose({
                                            kind: 'phoneNumber',
                                            phoneNumber: phoneCandidate,
                                        })
                                    }
                                    type="button"
                                >
                                    <span className="font-medium">
                                        {'Text ' +
                                            formatPhoneNumber(phoneCandidate)}
                                    </span>
                                    <span className="text-muted-foreground text-xs">
                                        Use this phone number
                                    </span>
                                </button>
                            ) : null}
                            {!clientError &&
                            !caregiverError &&
                            !clientRows.length &&
                            !caregivers.length &&
                            !phoneCandidate ? (
                                <p
                                    className="text-muted-foreground p-4"
                                    role="status"
                                >
                                    No matching people. Try the client’s name,
                                    the contact’s name, or a phone number.
                                </p>
                            ) : null}
                        </React.Fragment>
                    )}
                </div>
            ) : null}
        </div>
    );
}
